import React, { useCallback, useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { useGoogleMaps } from "../../context/GoogleMapsContext";
import InlineAlert from "../UI/DesignStandards/Notifications/InlineAlert";
import {
  ToggleCard,
  PrimaryButton,
  SecondaryButton,
  FormFieldsSkeleton,
} from "../UI/DesignStandards";
import {
  listBowlingCentersForTournament,
  organizationIdForTournament,
} from "../../utility/organizationBowlingCenters";
import { createSquadWithTemplates } from "../../utility/createSquad";
import {
  addTimeToDay,
  appendDay,
  dropDay,
  eventsForHouse,
  houseName,
  mergeEventsIntoBlocks,
  patchDay,
  removeTimeFromDay,
  squadsFromHouseBlocks,
  toggleAlsoEventOnDay,
  unusedHouseId,
  updateEventDays,
} from "../../utility/bulkSquadTree";
import HouseSquadBlock from "./HouseSquadBlock";

const BulkCreateSquads = ({
  tournamentID,
  eventsRevision = 0,
  onCreated,
  onCancel,
  openTemplatesByDefault = false,
}) => {
  const { user } = useAuth();
  const { isLoaded, loadError } = useGoogleMaps();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tournamentEvents, setTournamentEvents] = useState([]);
  const [orgHouses, setOrgHouses] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);
  const [eventTemplates, setEventTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [showAddHouse, setShowAddHouse] = useState(false);
  const [houseBlocks, setHouseBlocks] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const loadHouses = useCallback(async () => {
    const centers = await listBowlingCentersForTournament(tournamentID);
    setOrgHouses(centers);
    return centers;
  }, [tournamentID]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: eventsData, error: eventsError } = await supabase
          .from("tournamentEvents")
          .select("*")
          .eq("tournamentID", tournamentID)
          .eq("isPrimary", true)
          .eq("isDeleted", false);

        if (eventsError) throw eventsError;
        setTournamentEvents(eventsData || []);

        const { data: templatesData, error: templatesError } =
          await supabase.rpc("get_squad_event_templates", {
            p_tournament_id: tournamentID,
          });
        if (!templatesError) {
          setEventTemplates(templatesData || []);
          setSelectedTemplates([]);
        }

        const { data: tournament } = await supabase
          .from("tournaments")
          .select("startDate")
          .eq("id", tournamentID)
          .single();
        const tournamentStart = tournament?.startDate || "";
        setStartDate(tournamentStart);

        const [orgId, houses] = await Promise.all([
          organizationIdForTournament(tournamentID),
          loadHouses(),
        ]);
        setOrganizationId(orgId);
        setHouseBlocks([
          {
            locationId: houses.length === 1 ? houses[0].bowlingCenters.id : "",
            events: eventsForHouse(eventsData || [], tournamentStart),
          },
        ]);
        if (houses.length === 0) {
          setShowAddHouse(true);
        }
      } catch (error) {
        console.error("Error loading squad form:", error);
        setErrorMessage("Error fetching tournament data");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [tournamentID, loadHouses]);

  useEffect(() => {
    if (!eventsRevision) return;
    let cancelled = false;
    const refreshEvents = async () => {
      try {
        const { data: eventsData, error: eventsError } = await supabase
          .from("tournamentEvents")
          .select("*")
          .eq("tournamentID", tournamentID)
          .eq("isPrimary", true)
          .eq("isDeleted", false);
        if (cancelled || eventsError) return;
        const primaries = eventsData || [];
        setTournamentEvents(primaries);
        const onlyHouse =
          orgHouses.length === 1 ? orgHouses[0].bowlingCenters.id : "";
        setHouseBlocks((prev) =>
          mergeEventsIntoBlocks(prev, primaries, startDate, onlyHouse)
        );
      } catch (error) {
        console.error("Error refreshing squad events:", error);
      }
    };
    refreshEvents();
    return () => {
      cancelled = true;
    };
  }, [eventsRevision, tournamentID, startDate, orgHouses]);

  const updateHouse = (houseIndex, updater) => {
    setHouseBlocks((prev) =>
      prev.map((block, index) =>
        index === houseIndex ? updater(block) : block
      )
    );
  };

  const changeEventDays = (houseIndex, eventId, updater) => {
    updateHouse(houseIndex, (block) =>
      updateEventDays(block, eventId, updater)
    );
  };

  const addHouseBlock = () => {
    const usedIds = houseBlocks.map((block) => block.locationId);
    setHouseBlocks((prev) => [
      ...prev,
      {
        locationId: unusedHouseId(orgHouses, usedIds),
        events: eventsForHouse(tournamentEvents, startDate),
      },
    ]);
  };

  const handleCenterAdded = async (center) => {
    const houses = await loadHouses();
    const nextId =
      center?.id || houses[houses.length - 1]?.bowlingCenters?.id || "";
    setHouseBlocks((prev) =>
      prev.map((item, index) =>
        !item.locationId && (index === prev.length - 1 || prev.length === 1)
          ? { ...item, locationId: nextId }
          : item
      )
    );
    setShowAddHouse(false);
  };

  const squadsToCreate = squadsFromHouseBlocks(houseBlocks);
  const canCreate = squadsToCreate.length > 0;

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!canCreate) {
      setErrorMessage("Squad time, date, event, and location are required.");
      return;
    }
    setSaving(true);
    setErrorMessage("");
    try {
      for (const squad of squadsToCreate) {
        await createSquadWithTemplates({
          tournamentID,
          squadTime: squad.time,
          squadDate: squad.date,
          eventIds: squad.eventIds || [squad.eventId],
          squadLocation: squad.locationId,
          templateIds: selectedTemplates,
        });
      }
      onCreated();
    } catch (error) {
      console.error("Error creating squads:", error);
      setErrorMessage("Error saving squad");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <FormFieldsSkeleton fields={6} />;
  }

  return (
    <form onSubmit={handleCreate} className="space-y-5">
      {errorMessage ? (
        <InlineAlert
          type="error"
          title="Please fix the following"
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      ) : null}

      {loadError ? (
        <p className="text-sm text-brand-muted">
          Could not load Google Maps. Add a house from the organization bowling
          centers page.
        </p>
      ) : null}

      {tournamentEvents.length === 0 ? (
        <p className="text-sm text-brand-muted">
          Add a primary event before creating squads.
        </p>
      ) : (
        <div className="space-y-8">
          {houseBlocks.map((block, houseIndex) => (
            <HouseSquadBlock
              key={houseIndex}
              block={block}
              orgHouses={orgHouses}
              title={houseName(orgHouses, block.locationId)}
              canRemove={houseBlocks.length > 1}
              showAddCenter={houseIndex === 0 && isLoaded && !!organizationId}
              showAddCenterForm={showAddHouse}
              onToggleAddCenter={() => setShowAddHouse(!showAddHouse)}
              organizationId={organizationId}
              isLoggedIn={!!user}
              onCenterAdded={handleCenterAdded}
              onChangeLocation={(locationId) =>
                updateHouse(houseIndex, (current) => ({
                  ...current,
                  locationId,
                }))
              }
              onRemoveHouse={() =>
                setHouseBlocks((prev) =>
                  prev.length === 1
                    ? prev
                    : prev.filter((_, index) => index !== houseIndex)
                )
              }
              squadsToCreate={squadsToCreate}
              saving={saving}
              onChangeDate={(eventId, dayIndex, date) =>
                changeEventDays(houseIndex, eventId, (days) =>
                  patchDay(days, dayIndex, { date })
                )
              }
              onAddTime={(eventId, dayIndex, time) =>
                changeEventDays(houseIndex, eventId, (days) =>
                  addTimeToDay(days, dayIndex, time)
                )
              }
              onRemoveTime={(eventId, dayIndex, time) =>
                changeEventDays(houseIndex, eventId, (days) =>
                  removeTimeFromDay(days, dayIndex, time)
                )
              }
              onAddDay={(eventId) =>
                changeEventDays(houseIndex, eventId, appendDay)
              }
              onRemoveDay={(eventId, dayIndex) =>
                changeEventDays(houseIndex, eventId, (days) =>
                  dropDay(days, dayIndex)
                )
              }
              onToggleAlsoEvent={(eventId, dayIndex, time, alsoEventId) =>
                changeEventDays(houseIndex, eventId, (days) =>
                  toggleAlsoEventOnDay(days, dayIndex, time, alsoEventId)
                )
              }
            />
          ))}
          <SecondaryButton
            type="button"
            onClick={addHouseBlock}
            className="!px-3 !py-2 text-sm w-full sm:w-auto"
          >
            <FaPlus className="mr-2 h-4 w-4" />
            Add house
          </SecondaryButton>
        </div>
      )}

      {eventTemplates.length > 0 && (
        <details className="card-inset" defaultOpen={openTemplatesByDefault}>
          <summary className="cursor-pointer px-4 py-3 text-sm font-display font-bold text-brand-charcoal">
            Per-squad side pots
            <span className="ml-2 font-normal text-brand-muted">
              {selectedTemplates.length} selected
            </span>
          </summary>
          <p className="px-4 pb-3 text-sm text-brand-muted">
            Only events that need their own instance on each squad.
            Tournament-wide side pots are not attached here.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-4 pb-4">
            {eventTemplates.map((template) => {
              const isSelected = selectedTemplates.includes(template.id);
              return (
                <ToggleCard
                  key={template.id}
                  checked={isSelected}
                  onChange={() => {
                    if (isSelected) {
                      setSelectedTemplates(
                        selectedTemplates.filter((id) => id !== template.id)
                      );
                    } else {
                      setSelectedTemplates([...selectedTemplates, template.id]);
                    }
                  }}
                  title={template.event_name}
                  subtitle={
                    template.event_abbreviation
                      ? `(${template.event_abbreviation})`
                      : undefined
                  }
                />
              );
            })}
          </div>
        </details>
      )}

      <div className="flex justify-end gap-2 pt-1">
        {onCancel ? (
          <SecondaryButton
            type="button"
            onClick={onCancel}
            className="!px-3 !py-2 text-sm"
            disabled={saving}
          >
            Cancel
          </SecondaryButton>
        ) : null}
        <PrimaryButton type="submit" disabled={saving || !canCreate}>
          {saving
            ? "Saving..."
            : `Create ${squadsToCreate.length || ""} squad${
                squadsToCreate.length === 1 ? "" : "s"
              }`}
        </PrimaryButton>
      </div>
    </form>
  );
};

export default BulkCreateSquads;
